import {
  ApiRemoveAllDiscountCodesFromCartsFailedAction,
  ApiRemoveAllDiscountCodesFromCartsSucceededAction,
  apiRemoveAllDiscountCodesFromCartsFailedType,
  apiRemoveAllDiscountCodesFromCartsRequested,
  apiRemoveAllDiscountCodesFromCartsSucceededType
} from '@spa/lib/store/api/cart/removeAllDiscountCodesFromCartsApi';
import {
  RemoveAllDiscountCodesFromCartsRequestedAction,
  removeAllDiscountCodesFromCartsFailed,
  removeAllDiscountCodesFromCartsSucceeded,
  setCart
} from '../../carts/cartsSlice';
import { put, race, select, take } from 'typed-redux-saga';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import { StateCart } from '@spa/lib/store/carts/CartsState';
import convertCartToStateCart from '@spa/lib/converters/convertCartToStateCart';
import generateActionId from '@spa/lib/generateActionId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';

type RemoveResult =
  | { type: 'succeeded'; customOrderType: CustomOrderType; cart: StateCart | null }
  | { type: 'skipped' }
  | { type: 'failed' };

function* removeAllDiscountCodesFromCartIfPresent(
  customOrderType: CustomOrderType,
  requestedActionId: number
): Generator<unknown, RemoveResult> {
  const cart: StateCart | null = (yield* select(selectCarts))[customOrderType];

  if (cart === null) {
    return { type: 'skipped' };
  }

  const actionId: number = generateActionId();

  yield* put(
    apiRemoveAllDiscountCodesFromCartsRequested({
      actionId,
      customOrderType,
      parentActionId: requestedActionId
    })
  );

  let isSameActionId: boolean = false;
  let result: RemoveResult | undefined;

  do {
    const {
      succeeded,
      failed
    }: {
      succeeded: ApiRemoveAllDiscountCodesFromCartsSucceededAction | undefined;
      failed: ApiRemoveAllDiscountCodesFromCartsFailedAction | undefined;
    } = yield* race({
      succeeded: take<ApiRemoveAllDiscountCodesFromCartsSucceededAction>(
        apiRemoveAllDiscountCodesFromCartsSucceededType
      ),
      failed: take<ApiRemoveAllDiscountCodesFromCartsFailedAction>(
        apiRemoveAllDiscountCodesFromCartsFailedType
      )
    });

    isSameActionId =
      succeeded?.payload.requestedActionId === actionId ||
      failed?.payload.requestedActionId === actionId;

    if (isSameActionId) {
      if (succeeded !== undefined) {
        const rawCart = succeeded.payload.result?.cart ?? null;
        result = {
          type: 'succeeded',
          customOrderType,
          cart: rawCart ? convertCartToStateCart(rawCart) : null
        };
      } else {
        result = { type: 'failed' };
      }
    }
  } while (!isSameActionId);

  return result!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
}

export function* removeAllDiscountCodesFromCartsSaga({
  payload: { requestedActionId }
}: RemoveAllDiscountCodesFromCartsRequestedAction): Generator {
  const homeDeliveryResult: RemoveResult = yield* removeAllDiscountCodesFromCartIfPresent(
    CustomOrderType.HOME_DELIVERY,
    requestedActionId
  );

  const collectAtStoreResult: RemoveResult = yield* removeAllDiscountCodesFromCartIfPresent(
    CustomOrderType.COLLECT_AT_STORE,
    requestedActionId
  );

  const anyFailed = homeDeliveryResult.type === 'failed' || collectAtStoreResult.type === 'failed';

  for (const removeResult of [homeDeliveryResult, collectAtStoreResult]) {
    if (removeResult.type === 'succeeded' && removeResult.cart !== null) {
      yield* put(
        setCart({
          localChange: true,
          cart: removeResult.cart,
          customOrderType: removeResult.customOrderType
        })
      );
    }
  }

  const succeededOrderTypes: Array<CustomOrderType> = [homeDeliveryResult, collectAtStoreResult]
    .filter((r): r is Extract<RemoveResult, { type: 'succeeded' }> => r.type === 'succeeded')
    .map((r) => r.customOrderType);

  const failedOrderTypes: Array<CustomOrderType> = (
    [
      [homeDeliveryResult, CustomOrderType.HOME_DELIVERY],
      [collectAtStoreResult, CustomOrderType.COLLECT_AT_STORE]
    ] as [RemoveResult, CustomOrderType][]
  )
    .filter(([r]) => r.type === 'failed')
    .map(([, t]) => t);

  if (!anyFailed) {
    yield* put(
      removeAllDiscountCodesFromCartsSucceeded({
        requestedActionId,
        customOrderTypes: succeededOrderTypes
      })
    );
  } else {
    yield* put(
      removeAllDiscountCodesFromCartsFailed({
        requestedActionId,
        customOrderTypes: failedOrderTypes
      })
    );
  }
}

export default removeAllDiscountCodesFromCartsSaga;
